import React, { Dispatch, FC, SetStateAction, useState } from 'react';

import { GridSortModel } from '@mui/x-data-grid';
import { GridSortItem } from '@mui/x-data-grid/models/gridSortModel';

import { IObject } from '../../interfaces/IObject';

const initialFilter = {
  end_date: null,
  projects: null,
  title: null,
  groups: null,
  contract_type: null,
  contracting_parties: null,
  status: null,
};

interface IContextProps {
  activeTab: string;
  handleActiveTab: (value: string) => void;
  activeHeader: string;
  setActiveHeader: Dispatch<SetStateAction<string>>;
  filters: IObject;
  setFilters: Dispatch<SetStateAction<IObject>>;
  sorting: GridSortModel;
  setSorting: Dispatch<SetStateAction<GridSortItem[]>>;
  treeView: boolean;
  setTreeView: Dispatch<SetStateAction<boolean>>;
  filterNames: IObject;
  setFilterNames: Dispatch<SetStateAction<IObject>>;
}

const DocumentLibraryContext = React.createContext<IContextProps | null>(null);

export const useDocumentLibrary = (): IContextProps => {
  const context = React.useContext(DocumentLibraryContext);
  if (!context) {
    throw new Error(
      `useDocumentLibrary must be used within a DocumentLibraryProvider`
    );
  }

  const {
    activeTab,
    handleActiveTab,
    activeHeader,
    setActiveHeader,
    filters,
    setFilters,
    sorting,
    setSorting,
    setTreeView,
    treeView,
    filterNames,
    setFilterNames,
  } = context;

  return {
    activeTab,
    handleActiveTab,
    activeHeader,
    setActiveHeader,
    filters,
    sorting,
    setFilters,
    setSorting,
    treeView,
    setTreeView,
    filterNames,
    setFilterNames,
  };
};

interface IProps {
  children: React.ReactNode;
}

const DocumentLibraryProvider: FC<IProps> = (props) => {
  const [activeTab, setActiveTab] = useState('all');
  const [activeHeader, setActiveHeader] = useState<string>('active');
  const [treeView, setTreeView] = useState<boolean>(false);
  const [filters, setFilters] = useState<IObject>(initialFilter);
  const [sorting, setSorting] = useState<GridSortItem[]>([]);
  const [filterNames, setFilterNames] = useState<IObject>({});

  const handleActiveTab = (value: any) => {
    switch (value) {
      case 'expired': {
        setActiveHeader('expired');
        setActiveTab('all');
        setTreeView(false);
        break;
      }
      case 'terminated': {
        setActiveHeader('terminated');
        setTreeView(false);
        break;
      }
      case 'active': {
        setActiveTab('all');
        setActiveHeader('active');
        setTreeView(false);
        break;
      }
      case 'contracts_count':
      case 'all': {
        setActiveHeader('contracts_count');
        setActiveTab('all');
        break;
      }
      case 'expiring_this_quarter': {
        setActiveHeader('expiring_this_quarter');
        setActiveTab('all');
        setTreeView(false);
        break;
      }
      case 'expiring_next_quarter': {
        setActiveHeader('expiring_next_quarter');
        setActiveTab('all');
        setTreeView(false);
        break;
      }
      case 'expiring': {
        setActiveHeader('expiring');
        setActiveTab('all');
        setTreeView(false);
        break;
      }
      case 'total_contracts_size': {
        break;
      }
      default: {
        setActiveTab(value);
        setTreeView(false);
      }
    }
    setFilters(initialFilter);
  };

  const value = React.useMemo(
    () => ({
      activeTab,
      handleActiveTab,
      activeHeader,
      setActiveHeader,
      filters,
      setFilters,
      sorting,
      setSorting,
      treeView,
      setTreeView,
      filterNames,
      setFilterNames,
    }),
    [activeTab, activeHeader, filters, sorting, treeView, filterNames]
  );

  return <DocumentLibraryContext.Provider value={value} {...props} />;
};

export default DocumentLibraryProvider;
